import init, * as raw from "../pkg/rhema_crypto.js";

export { raw };

/** Anything wasm-bindgen accepts: a URL or path to the .wasm file, its bytes, a Response or a compiled module. */
export type WasmSource = string | URL | Request | Response | BufferSource | WebAssembly.Module;

let loading: Promise<void> | undefined;
let ready = false;

/**
 * Loads the wasm module. Call once before using anything else. With no argument the
 * module is fetched from next to the JS glue, which Vite resolves at build time.
 */
export function initCrypto(source?: WasmSource): Promise<void> {
  if (!loading) {
    loading = init(source === undefined ? undefined : { module_or_path: source }).then(
      () => {
        ready = true;
      },
      (error: unknown) => {
        loading = undefined;
        throw error;
      },
    );
  }
  return loading;
}

export function assertReady(): void {
  if (!ready) throw new Error("@rhema/crypto is not initialised. Await initCrypto() first.");
}

interface Freeable {
  free(): void;
}

// Safety net only. Callers should still call free() when they are done.
const registry = new FinalizationRegistry<Freeable>((inner) => {
  try {
    inner.free();
  } catch {
    // Already freed.
  }
});

export abstract class Handle<T extends Freeable> {
  #inner: T | undefined;

  protected constructor(inner: T) {
    this.#inner = inner;
    registry.register(this, inner, this);
  }

  protected get inner(): T {
    if (!this.#inner) throw new Error(`${this.constructor.name} has been freed`);
    return this.#inner;
  }

  /** Releases the wasm memory. The object cannot be used afterwards. */
  free(): void {
    if (!this.#inner) return;
    registry.unregister(this);
    this.#inner.free();
    this.#inner = undefined;
  }
}

/** Runs `use` on a short lived wasm value and frees it afterwards. */
export function consume<T extends Freeable, R>(value: T, use: (value: T) => R): R {
  try {
    return use(value);
  } finally {
    value.free();
  }
}

export function checkPickleKey(key: Uint8Array): void {
  if (!(key instanceof Uint8Array) || key.length !== 32) {
    throw new Error("pickle key must be a 32 byte Uint8Array");
  }
}
